/**
 * Joining the global Stronger Memory group — the platform-free part of `useGlobalGroup`.
 *
 * v1 has no groups a person can pick: everyone is a member of one PUBLIC "Stronger Memory"
 * group (2026-09-02 sync, Andrew in Slack). Core's `POST /api/v1/groups/{uuid}/membership`
 * is idempotent, so the app simply calls it whenever a session starts and reads the answer:
 *
 * - `member`      — joined now, or already in.
 * - `unavailable` — the group isn't there (404: not created in this environment, or made
 *                   SECRET) or isn't open (403: made PRIVATE). Nothing to retry; the app
 *                   works as before, only the group features stay off.
 *
 * Anything else (401 mid-refresh, 5xx, network) is thrown so the caller can retry.
 */
export type GroupMembershipStatus = 'member' | 'unavailable';

export interface GroupMembership {
  status: GroupMembershipStatus;
  /** Role names the caller holds in the group ("Admin", "Member"); empty when unavailable. */
  roles: string[];
}

export interface JoinGroupResult {
  status: number;
  data?: unknown;
}

/** Core's Admin role — shown as "Leader" in Stronger Memory (PRD-1945 titles) until Core has its own. */
export const GROUP_ADMIN_ROLE = 'Admin';

export function isGroupLeader(membership: GroupMembership | undefined): boolean {
  return membership?.roles.includes(GROUP_ADMIN_ROLE) ?? false;
}

function rolesOf(data: unknown): string[] {
  const roles =
    typeof data === 'object' && data !== null && 'membership' in data
      ? (data as { membership?: { roles?: unknown } }).membership?.roles
      : undefined;
  return Array.isArray(roles)
    ? roles.filter((role): role is string => typeof role === 'string')
    : [];
}

export async function ensureGroupMembership(
  join: () => Promise<JoinGroupResult>,
): Promise<GroupMembership> {
  let response: JoinGroupResult;
  try {
    response = await join();
  } catch (error) {
    // The api client throws for a non-2xx answer (`ApiError`), with the status on it.
    if (isUnavailable(statusOf(error))) return { status: 'unavailable', roles: [] };
    throw error;
  }
  if (response.status === 200) return { status: 'member', roles: rolesOf(response.data) };
  if (isUnavailable(response.status)) return { status: 'unavailable', roles: [] };
  throw new Error(`OnEarly: joining the group failed with status ${response.status}`);
}

function isUnavailable(status: number | undefined): boolean {
  return status === 403 || status === 404;
}

function statusOf(error: unknown): number | undefined {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status?: unknown }).status
      : undefined;
  return typeof status === 'number' ? status : undefined;
}
